/**
 * Unit status helpers — keeps the kenttänäkymä logic in ONE place.
 *
 * Mapping (UI -> data):
 *   aloittamatta -> "pending"     (default, no content yet)
 *   kesken       -> "in_progress" (user touched it but didn't finish)
 *   valmis       -> "done"        (user marked complete)
 *   estynyt      -> "inaccessible" (special, kept for backward compat)
 *
 * The "kesken" state is auto-derived from content: if the unit has any
 * description, additional_work_text, checked checklist item, photo, or
 * material — and the user hasn't explicitly marked it done/inaccessible —
 * we treat it as in_progress for display purposes WITHOUT mutating the DB.
 * The user-facing "Merkitse valmiiksi" is the only way to flip to "done".
 */

export function hasAnyContent(u) {
  if (!u) return false;
  if ((u.description || "").trim()) return true;
  if ((u.additional_work_text || "").trim()) return true;
  if ((u.notes || "").trim()) return true;
  if ((u.checklist || []).some((c) => c.checked)) return true;
  if ((u.photos || []).length > 0) return true;
  if ((u.materials_used || []).length > 0) return true;
  return false;
}

export function derivedStatus(u) {
  if (!u) return "pending";
  if (u.status === "done") return "done";
  if (u.status === "inaccessible") return "inaccessible";
  return hasAnyContent(u) ? "in_progress" : "pending";
}

export const STATUS_LABEL = {
  pending: "Aloittamatta",
  in_progress: "Kesken",
  done: "Valmis",
  inaccessible: "Estynyt",
};

export const STATUS_COLOR = {
  pending: { dot: "bg-slate-300", text: "text-slate-500", row: "border-slate-200" },
  in_progress: { dot: "bg-amber-500", text: "text-amber-700", row: "border-amber-200" },
  done: { dot: "bg-emerald-500", text: "text-emerald-700", row: "border-emerald-200" },
  inaccessible: { dot: "bg-rose-500", text: "text-rose-700", row: "border-rose-200" },
};

/**
 * Returns a list of human-readable warnings for a unit when marking the whole
 * report as complete. Warnings are advisory only — never blockers.
 */
export function unitCompletionWarnings(u) {
  if (!u) return [];
  const ds = derivedStatus(u);
  if (ds === "inaccessible") return [];
  const warnings = [];
  if (!hasAnyContent(u)) {
    warnings.push("Aloittamatta — ei kuvia eikä merkintöjä");
    return warnings;
  }
  // Todellinen sisältöpuute — ei enää "Ei merkitty valmiiksi" -fallbackia
  // koska per-yksikkö "valmis" -konsepti on poistettu käyttäjän UI:sta.
  const hasUnchecked = (u.checklist || []).some((c) => !c.checked && !c.is_finding);
  const hasUncheckedFindings = (u.checklist || []).some((c) => !c.checked && c.is_finding);
  if (hasUnchecked) warnings.push("Työvaiheita merkitsemättä");
  if (hasUncheckedFindings) warnings.push("Puutechecklist täyttämättä");
  if ((u.photos || []).length === 0) warnings.push("Ei kuvia");
  return warnings;
}

/**
 * Aggregate progress stats from a report.units array — used for the progress
 * card on top of the unit list (18/28 valmista jne).
 */
export function reportProgress(units = []) {
  const total = units.length;
  let done = 0;
  let inProgress = 0;
  let pending = 0;
  let inaccessible = 0;
  for (const u of units) {
    const s = derivedStatus(u);
    if (s === "done") done += 1;
    else if (s === "in_progress") inProgress += 1;
    else if (s === "inaccessible") inaccessible += 1;
    else pending += 1;
  }
  const completed = done + inaccessible;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  return { total, done, inProgress, pending, inaccessible, completed, percent };
}
